// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * A table component for displaying return order information with filtering.
 */
import { EmptyState } from '@/components/empty-state';
import { TablePaginationFooter } from '@/components/table-pagination-footer';
import { ArrowOutwardRounded } from '@/constants/hardfin-standard-icons';
import { Sort } from '@/constants/sort';
import { useTableSort } from '@/hooks/use-table-sort';
import { getFilterStatuses } from '@/return-order/api/get-return-orders';
import { ReturnOrderTableRow } from '@/return-order/components/return-order-table-row';
import { ReturnOrderFilter } from '@/return-order/constants/status';
import {
  ReturnOrderSortProperty,
  useReturnOrders,
} from '@/return-order/hooks/use-return-orders';
import {
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from '@mui/material';
import { useCallback, useEffect } from 'react';

/**
 * Props for the ReturnOrderTable component.
 */
interface Props {
  filter?: ReturnOrderFilter;
  sort: Sort<ReturnOrderSortProperty>;
  onSort: (property: ReturnOrderSortProperty) => void;
  onPageChange: (pageNumber: number) => void;
}

/**
 * A table component for displaying return order information with filtering.
 */
export function ReturnOrderTable(props: Props) {
  const { returnOrders, resultCount } = useReturnOrders({
    filter: props.filter,
  });

  const pageNumber = props.filter?.page ?? 1;
  const pageSize = props.filter?.pageSize ?? 20;

  const onPageChange = props.onPageChange;
  const onSort = props.onSort;

  const downloadURL = new URL(
    '/v1/export/return-orders',
    import.meta.env.VITE_API_URL
  );
  const statuses = getFilterStatuses(
    props.filter?.status,
    props.filter?.subStatuses
  );
  if (statuses) {
    for (const s of statuses) {
      downloadURL.searchParams.append('statuses', s);
    }
  }
  if (props.filter?.searchTerm) {
    downloadURL.searchParams.set('search', props.filter.searchTerm);
  }

  /**
   * Table sort helpers for managing sort indicators.
   */
  const { getSortDirection, getLabelDirection, isLabelActive } = useTableSort(
    props.sort
  );

  /**
   * Creates a sort handler for a specific column.
   */
  const createSortHandler = useCallback(
    (property: ReturnOrderSortProperty) => () => {
      onSort(property);
    },
    [onSort]
  );

  // Handle page overrun
  useEffect(() => {
    if (onPageChange && returnOrders.length === 0 && pageNumber > 1) {
      onPageChange(1);
    }
  }, [returnOrders.length, pageNumber, onPageChange]);

  if (returnOrders.length === 0) {
    return (
      <EmptyState title="No return orders found">
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography color="text.secondary">
            To start tracking return orders in Hardfin, add a new return order
          </Typography>
          <ArrowOutwardRounded fontSize="small" color="action" />
        </Stack>
      </EmptyState>
    );
  }

  return (
    <>
      <TableContainer component={Paper} variant="outlined">
        <Table sx={{ tableLayout: 'fixed' }}>
          <TableHead>
            <TableRow>
              <TableCell
                width="25%"
                sortDirection={getSortDirection('roNumber')}
              >
                <TableSortLabel
                  active={isLabelActive('roNumber')}
                  direction={getLabelDirection('roNumber')}
                  onClick={createSortHandler('roNumber')}
                >
                  References
                </TableSortLabel>
              </TableCell>
              <TableCell width="25%">From/To location</TableCell>
              <TableCell width="30%">Items</TableCell>
              <TableCell width="20%" sortDirection={getSortDirection('status')}>
                <TableSortLabel
                  active={isLabelActive('status')}
                  direction={getLabelDirection('status')}
                  onClick={createSortHandler('status')}
                >
                  Status
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {returnOrders.map((returnOrder) => (
              <ReturnOrderTableRow
                key={returnOrder.id}
                returnOrder={returnOrder}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePaginationFooter
        pagination={{
          page: pageNumber,
          pageSize,
          onPageChange,
        }}
        totalCount={resultCount}
        entityOptions={{ singular: 'return order', includeCount: false }}
        downloadURL={downloadURL}
        downloadLabel="Export return order data"
      />
    </>
  );
}
